import { useTranslation } from "react-i18next";
// ─── PerformanceMetricsPicker.tsx ─────────────────────────────────────────────
import { DeleteIcon } from "@/components/icons/action-icons";
import { Card } from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  SearchIcon,
  XIcon,
} from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useOnInView } from "react-intersection-observer";
import { useDebounceValue } from "usehooks-ts";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/v2/spinner";
import { ShowWhen } from "@/components/ui/v2/show-when";
import type { FormikProps } from "formik";
import { cn } from "@/utils/cn";
import type { Fields } from "../manage/configuration/configuration-form";
import type { IPerformanceMetric } from "@/types/(company)/performance.interface";
import {
  useGetPaginatedPerformanceMetricsQuery,
  useGetPerformanceMetricsInfiniteQuery,
} from "@/services/(company)/api.performance";

interface IProps {
  formik: FormikProps<Fields>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
}

const fieldName = "performanceMetrics" satisfies keyof Fields;

export const PerformanceMetricsPicker = ({ formik, className }: IProps) => {
  const { t } = useTranslation(["common", "company"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 500);
  const [isOpen, setIsOpen] = useState(false);

  // ─── Infinite query ───────────────────────────────────────────────────────
  const {
    fetchNextPage,
    data: apiResponse,
    isFetchingNextPage,
    isFetching: isFetchingMetrics,
    isLoading: isFirstLoading,
  } = useGetPerformanceMetricsInfiniteQuery({ search: debouncedSearch });

  const { data: allPerformanceMetric } = useGetPaginatedPerformanceMetricsQuery(
    { limit: 100 },
  );

  const allMetrics: IPerformanceMetric[] =
    apiResponse?.pages.flatMap((page) => page.data) ?? [];
  const totalItems = apiResponse?.pages[0]?.meta?.totalItems ?? 0;

  // ─── Selection helpers ────────────────────────────────────────────────────
  const selectedMetrics = formik.values[fieldName] ?? [];
  const selectedMetricIds = selectedMetrics.map((m) => m.metricId);
  const hasSelected = selectedMetrics.length > 0;
  const fieldError = formik.errors[fieldName];
  const hasError = Boolean(fieldError && formik.touched[fieldName]);

  const hasWeightageError = Array.isArray(fieldError)
    ? fieldError.some((e) => e && typeof e === "object" && "weightage" in e)
    : false;

  const visibleMetricIds = allMetrics.map((m) => m.id);
  const selectedFromVisible = selectedMetrics.filter((m) =>
    visibleMetricIds.includes(m.metricId),
  );
  const allVisibleSelected =
    visibleMetricIds.length > 0 &&
    visibleMetricIds.every((id) => selectedMetricIds.includes(id));

  const handleToggle = (metric: IPerformanceMetric) => {
    const isSelected = selectedMetricIds.includes(metric.id);
    const next = isSelected
      ? selectedMetrics.filter((m) => m.metricId !== metric.id)
      : [...selectedMetrics, { metricId: metric.id, weightage: 0 }];
    formik.setFieldValue(fieldName, next);
  };

  const handleWeightageChange = (metricId: number, value: number) => {
    formik.setFieldValue(
      fieldName,
      selectedMetrics.map((m) =>
        m.metricId === metricId ? { ...m, weightage: value } : m,
      ),
    );
  };

  const handleRemove = (metricId: number) => {
    formik.setFieldValue(
      fieldName,
      selectedMetrics.filter((m) => m.metricId !== metricId),
    );
  };

  const handleSelectAll = () => {
    const newSelections = allMetrics
      .filter((m) => !selectedMetricIds.includes(m.id))
      .map((m) => ({ metricId: m.id, weightage: 0 }));

    formik.setFieldValue(fieldName, [...selectedMetrics, ...newSelections]);
  };

  const clearSelection = () => {
    formik.setFieldValue(
      fieldName,
      selectedMetrics.filter((m) => !visibleMetricIds.includes(m.metricId)),
    );
  };
  const clearSearchQuery = () => setSearchQuery("");

  const handleConfirm = () => {
    setIsOpen(false);
    clearSearchQuery();
  };

  const infiniteScrollAnchor = useOnInView((inView) => {
    if (inView) fetchNextPage();
  });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Card
        className={cn("p-3 rounded-xs", hasError && "border-danger", className)}
      >
        <FieldGroup>
          <Field>
            <FieldLabel className="uppercase text-xs tracking-wide text-gray-500">
              {t("company:performance.select_metrics_label")}
            </FieldLabel>
            <InputGroup className={cn("h-10", hasError && "border-danger")}>
              <InputGroupAddon>
                <Search className="text-black/50" />
              </InputGroupAddon>
              <InputGroupInput
                className="cursor-pointer"
                placeholder={t("common:placeholders.select_metrics_name")}
                readOnly
                onClick={() => setIsOpen(true)}
              />
            </InputGroup>
          </Field>
        </FieldGroup>

        {/* Selected metrics table */}
        <ShowWhen when={hasSelected}>
          <div className="mt-3">
            <p className="text-xs font-semibold text-primary mb-3">
              {t("company:performance.metrics_selected_count", {
                count: selectedMetrics.length,
              })}
            </p>
            <div className="overflow-hidden space-y-2">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_160px_40px] px-1 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <span>
                  {t("company:performance.metrics_table_header_metric")}
                </span>
                <span>
                  {t("company:performance.metrics_table_header_weightage")}
                </span>
                <span />
              </div>
              {selectedMetrics.map((item) => {
                const metric = allPerformanceMetric?.data.find(
                  (m) => m.id === item.metricId,
                );
                return (
                  <div
                    key={item.metricId}
                    className="grid grid-cols-[1fr_160px_40px] gap-2 items-center"
                  >
                    <span className="text-sm text-gray-700 px-4 py-3 bg-gray-100 rounded-xs border">
                      {metric?.title ?? "-"}
                    </span>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={item.weightage}
                      onChange={(e) =>
                        handleWeightageChange(
                          item.metricId,
                          Number(e.target.value),
                        )
                      }
                      className="h-11 text-sm w-full"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemove(item.metricId)}
                      className="text-red-400 hover:text-red-600 flex items-center justify-center p-3 rounded-xs border"
                    >
                      <DeleteIcon size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </ShowWhen>

        <FieldError className="mt-1.5">
          {hasError && typeof fieldError === "string"
            ? fieldError
            : hasWeightageError
              ? t("company:performance.weightage_positive_error")
              : null}
        </FieldError>
      </Card>

      {/* ─── Dialog ──────────────────────────────────────────────────────── */}
      <Dialog
        open={isOpen}
        onOpenChange={() => {
          setIsOpen((o) => !o);
          clearSearchQuery();
        }}
      >
        <DialogContent showCloseButton={false} aria-describedby={undefined}>
          <DialogHeader className="flex-row items-center justify-between">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setIsOpen(false)}
                className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0"
              >
                <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
              </button>
              <DialogTitle>
                {t("company:performance.select_metrics_label")}
              </DialogTitle>
            </div>
            {hasSelected && (
              <button className="text-primary text-sm" onClick={clearSelection}>
                {t("company:performance.clear_selection")}
              </button>
            )}
          </DialogHeader>

          {/* Search */}
          <InputGroup>
            <InputGroupAddon>
              <SearchIcon className="text-black/50" />
            </InputGroupAddon>
            <InputGroupInput
              value={searchQuery}
              placeholder={t("common:placeholders.search_metrics")}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroupAddon align="inline-end">
              {isFetchingMetrics && <Spinner size="sm" />}
              {!isFetchingMetrics && searchQuery.length > 0 && (
                <button onClick={clearSearchQuery} className="px-1">
                  <XIcon size={16} className="text-black/50" />
                </button>
              )}
            </InputGroupAddon>
          </InputGroup>

          {/* Count + select all */}
          <ShowWhen when={allMetrics.length > 0}>
            <div className="flex justify-between items-center text-sm">
              <div className="flex gap-1">
                <p className="text-gray-500">
                  {t("company:performance.metrics_count_label", {
                    count: totalItems,
                  })}
                </p>
                {selectedFromVisible.length > 0 && (
                  <p className="font-semibold">
                    {t("company:performance.metrics_selected_from_visible", {
                      count: selectedFromVisible.length,
                    })}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-xs! text-primary hover:underline"
                onClick={handleSelectAll}
                disabled={allVisibleSelected}
              >
                {t("company:performance.select_all")}
              </Button>
            </div>
          </ShowWhen>

          {/* List */}
          <ul className="min-h-80 max-h-80 overflow-y-scroll space-y-2">
            {isFirstLoading ? (
              <Spinner />
            ) : allMetrics.length ? (
              <>
                {allMetrics.map((metric) => (
                  <li
                    key={metric.id}
                    role="button"
                    className="flex items-center justify-between hover:bg-primary/10 rounded-xs p-2 border"
                    onClick={() => handleToggle(metric)}
                  >
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        checked={selectedMetricIds.includes(metric.id)}
                        onCheckedChange={() => handleToggle(metric)}
                      />
                      <div className="flex flex-col">
                        <p className="text-sm text-gray-800">{metric.title}</p>
                      </div>
                    </div>
                    <ChevronRight className="text-primary shrink-0" />
                  </li>
                ))}
                <div ref={infiniteScrollAnchor} />
                {isFetchingNextPage && <Spinner wrapperClassName="h-5" />}
              </>
            ) : (
              <small className="block text-gray-400">
                {t("company:performance.no_metrics_found")}
              </small>
            )}
          </ul>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                {t("common:buttons.cancel")}
              </Button>
            </DialogClose>
            <Button type="button" onClick={handleConfirm}>
              {t("company:performance.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
